import React, { useState, useEffect, useMemo } from 'react';
import { policyBaselineScripts } from './BuntuDashboard';
import PolicyDeviceCards from './PolicyDeviceCards';
import DeploymentModal from './DeploymentModal';
import PolicyBuilderModal from './PolicyBuilderModal';
import { apiService } from '../services/api';
import { saveBaselineDeployment, getTargetDisplayName } from '../utils/baselineDeployments';
import { deviceMatchesOS } from '../utils/devicePlatformMatch';

/**
 * Standard Baseline Policies view: same as BuntuDashboard Baselines > Standard Baseline > Policies.
 * Shows PolicyDeviceCards (platform policy list, Run / Create policy) with DeploymentModal and PolicyBuilderModal.
 *
 * @param {{ allowedPlatforms?: string[] | null }} [props]
 */
function StandardPoliciesView(props) {
  const allowedPlatforms = props?.allowedPlatforms ?? null;
  const [devices, setDevices] = useState([]);
  const [deviceGroups, setDeviceGroups] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [policyModal, setPolicyModal] = useState({ isOpen: false, deviceType: null, baselineType: null });
  const [customPolicies, setCustomPolicies] = useState({ general: {} });
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiService.getDevices().then((data) => {
      if (!cancelled) setDevices(Array.isArray(data) ? data : []);
    }).catch(() => { if (!cancelled) setDevices([]); });
    return () => { cancelled = true; };
  }, []);

  const deviceGroupsMemo = useMemo(() => {
    const platforms = Array.from(new Set(devices.map((d) => d.platform).filter(Boolean))).map((p) => ({
      id: p,
      name: String(p).replace(/^plat-/, ''),
      type: 'platform',
    }));
    ['plat-windows', 'plat-linux', 'plat-macos', 'plat-android', 'plat-ios', 'go-agent'].forEach((p) => {
      if (!platforms.find((x) => x.id === p)) {
        platforms.push({ id: p, name: p.replace(/^plat-/, ''), type: 'platform' });
      }
    });
    const depts = Array.from(new Set(devices.map((d) => d.dept || 'Unassigned'))).map((d) => ({
      id: d === 'Unassigned' ? 'Unassigned' : d,
      name: d,
      type: 'dept',
    }));
    return [
      {
        id: 'root',
        children: [
          { id: 'cat-depts', children: depts },
          { id: 'cat-platforms', children: platforms },
        ],
      },
    ];
  }, [devices]);

  useEffect(() => {
    setDeviceGroups(deviceGroupsMemo);
  }, [deviceGroupsMemo]);

  const handleDeployClick = (script) => {
    setSelectedScript(script);
    setModalOpen(true);
  };

  const openPolicyBuilder = (deviceType, baselineType = null) => {
    setPolicyModal({ isOpen: true, deviceType, baselineType });
  };

  const closePolicyBuilder = () => {
    setPolicyModal({ isOpen: false, deviceType: null, baselineType: null });
  };

  const handlePolicySave = (policy) => {
    if (!policy?.deviceType) {
      closePolicyBuilder();
      return;
    }
    setCustomPolicies((prev) => {
      const next = { ...prev };
      const general = next.general || {};
      const key = policy.deviceType;
      next.general = { ...general, [key]: [...(general[key] || []), policy] };
      return next;
    });
    setNotification({ title: 'Policy saved', msg: `${policy.title} is ready for deployment.`, type: 'success' });
    setTimeout(() => setNotification(null), 3000);
    closePolicyBuilder();
  };

  const getDeviceId = (d) => d.device_id || d.id;

  const confirmDeployment = async (targetInfo) => {
    setModalOpen(false);
    const { targetType, targetId, params, description } = targetInfo;
    const scriptDeviceType = selectedScript?.deviceType;

    setNotification({ title: 'Deployment Queued', msg: `Script '${selectedScript?.title}' deploying to ${description}...`, type: 'success' });

    let targetDeviceIds = [];
    if (targetType === 'single') {
      targetDeviceIds = [targetId];
    } else if (targetType === 'multiple') {
      targetDeviceIds = targetId.split(',').map((s) => s.trim()).filter(Boolean);
    } else if (targetType === 'collection') {
      if (targetId.startsWith('plat-') || targetId === 'go-agent') {
        targetDeviceIds = devices.filter((d) => targetId === 'go-agent' ? true : deviceMatchesOS(d, targetId)).map(getDeviceId);
      } else {
        targetDeviceIds = devices.filter((d) => (d.dept || 'Unassigned') === targetId).map(getDeviceId);
      }
      if (targetDeviceIds.length === 0) {
        targetDeviceIds = devices.map(getDeviceId);
      }
    }

    const platformMap = { windows: 'plat-windows', android: 'plat-android', macos: 'plat-macos', linux: 'plat-linux', ios: 'plat-ios' };
    const requiredPlatform = scriptDeviceType ? platformMap[scriptDeviceType] : null;
    if (requiredPlatform) {
      const compatibleIds = new Set(devices.filter((d) => deviceMatchesOS(d, requiredPlatform) || d.platform === scriptDeviceType).map(getDeviceId));
      targetDeviceIds = targetDeviceIds.filter((id) => compatibleIds.has(id));
      if (targetDeviceIds.length === 0) {
        setNotification({ title: 'No Compatible Devices', msg: `No ${scriptDeviceType} devices in the selected target.`, type: 'error' });
        setTimeout(() => setNotification(null), 5000);
        return;
      }
    }

    let successCount = 0;
    let errorCount = 0;
    for (const deviceId of targetDeviceIds) {
      try {
        const device = devices.find((d) => getDeviceId(d) === deviceId);
        const devicePlatform = device?.platform || '';

        if (scriptDeviceType === 'android') {
          const policyId = selectedScript?.id;
          if (policyId === 'android-wipe') {
            await apiService.wipeAndroidDevice(deviceId, false);
          } else if (policyId === 'android-lock') {
            await apiService.lockAndroidDevice(deviceId);
          } else {
            await apiService.syncAndroidPolicy(deviceId);
          }
          successCount++;
        } else if (scriptDeviceType === 'ios') {
          await apiService.queueBuntuMDMCommand(deviceId, 'DeviceInformation', params || {});
          successCount++;
        } else {
          let commandType = 'powershell';
          if (selectedScript?.lang === 'Bash' || selectedScript?.lang === 'Shell') commandType = 'bash';
          else if (selectedScript?.lang === 'PowerShell') commandType = 'powershell';
          await apiService.executeGoAgentCommand(deviceId, {
            command_type: commandType,
            command: selectedScript?.content || '# No content',
            parameters: {
              ...(params || {}),
              source: 'policies_compliance',
              policy_key: selectedScript?.id || null,
              script_id: selectedScript?.id || null,
              policy_title: selectedScript?.title || null,
              target_type: targetType || null,
              target_id: targetId || null,
            },
            timeout_seconds: 600,
            created_by: 'standard-policies',
          });
          successCount++;
        }
      } catch (e) {
        errorCount++;
      }
    }

    setNotification({
      title: 'Deployment complete',
      msg: `Success: ${successCount}, Failed: ${errorCount}`,
      type: errorCount > 0 ? 'error' : 'success',
    });
    setTimeout(() => setNotification(null), 5000);

    // Persist deployment record for Per Collection Summary
    if (targetType === 'collection' && selectedScript) {
      saveBaselineDeployment({
        scriptId: selectedScript.id,
        scriptTitle: selectedScript.title,
        targetType,
        targetId,
        targetName: getTargetDisplayName(targetId),
        deviceIds: targetDeviceIds,
        timestamp: new Date().toISOString(),
        successCount,
        errorCount,
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-6">
        {notification && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${notification.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
            <strong>{notification.title}</strong> — {notification.msg}
          </div>
        )}
        <PolicyDeviceCards
          scripts={policyBaselineScripts}
          onSelectScript={handleDeployClick}
          onCreatePolicy={(deviceType) => openPolicyBuilder(deviceType)}
          customPolicies={customPolicies.general || {}}
          allowedPlatforms={allowedPlatforms}
          initialSelectedDeviceType={
            Array.isArray(allowedPlatforms) && allowedPlatforms.length
              ? allowedPlatforms[0]
              : 'windows'
          }
        />
      </div>

      <DeploymentModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedScript(null); }}
        script={selectedScript}
        preSelectedTarget={null}
        onConfirm={confirmDeployment}
        deviceGroups={deviceGroups}
        devices={devices}
      />

      <PolicyBuilderModal
        isOpen={policyModal.isOpen}
        deviceType={policyModal.deviceType}
        baselineType={policyModal.baselineType}
        onClose={closePolicyBuilder}
        onSave={handlePolicySave}
      />
    </div>
  );
}

export default StandardPoliciesView;
